import {
  ApiOutlined,
  DashboardOutlined,
  FileTextOutlined,
  HomeOutlined,
  LineChartOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import type { AdminUser } from "@features/users/api";
import { resolveUserRole } from "@shared/rbac";
import type { MenuProps } from "antd";
import type { ReactNode } from "react";

type MenuItem = Required<MenuProps>["items"][number];

export interface NavSection {
  key: string;
  icon: ReactNode;
  label: string;
  defaultPath: string;
  children?: MenuItem[];
  tourId?: string;
  /** If set, opens URL in a new tab instead of navigating within the app */
  externalUrl?: string;
}

export const navSections: NavSection[] = [
  {
    key: "/dashboard",
    icon: <DashboardOutlined />,
    label: "Главная",
    defaultPath: "/dashboard",
    tourId: "nav-dashboard",
  },
  {
    key: "/assets",
    icon: <HomeOutlined />,
    label: "Активы",
    defaultPath: "/assets/objects",
    children: [
      { key: "/assets/objects", label: "Объекты" },
      { key: "/assets/devices", label: "Устройства" },
      { key: "/assets/connections", label: "Подключения" },
    ],
  },
  {
    key: "/monitoring",
    icon: <LineChartOutlined />,
    label: "Мониторинг",
    defaultPath: "/monitoring",
    children: [
      { key: "/monitoring", label: "Графики" },
      { key: "/monitoring/last-data", label: "Последние" },
      { key: "/monitoring/network", label: "Сеть" },
      { key: "/monitoring/network-telemetry", label: "Телеметрия сети" },
      { key: "/monitoring/vectors", label: "Вектора" },
      { key: "/monitoring/events", label: "События" },
    ],
  },
  {
    key: "/analytics",
    icon: <FileTextOutlined />,
    label: "Аналитика",
    defaultPath: "/analytics/readings",
    children: [
      { key: "/analytics/readings", label: "Показания" },
      { key: "/analytics/tou", label: "Текущие" },
      { key: "/analytics/autoread", label: "Суточные" },
      { key: "/analytics/profile", label: "Профиль" },
      { key: "/analytics/power-quality", label: "Качество" },
      { key: "/analytics/matrix", label: "Матричные" },
      { key: "/analytics/regulatory", label: "Отчёты 80020" },
      { key: "/analytics/ai-analysis", label: "ИИ-анализ" },
    ],
  },
  {
    key: "/comms",
    icon: <ApiOutlined />,
    label: "Диагностика связи",
    defaultPath: "/comms/state",
    children: [
      { key: "/comms/state", label: "Статус" },
      { key: "/comms/errors", label: "Ошибки" },
      { key: "/comms/outages", label: "Нет связи" },
      { key: "/comms/drift", label: "Время" },
      { key: "/comms/events", label: "События" },
      { key: "/comms/polling-log", label: "Журнал опроса" },
    ],
  },
  {
    key: "/support",
    icon: <MessageOutlined />,
    label: "Поддержка",
    defaultPath: "/support/tickets",
    children: [{ key: "/support/tickets", label: "Тикеты" }],
    tourId: "nav-support",
  },
  {
    key: "/admin",
    icon: <TeamOutlined />,
    label: "Администрирование",
    defaultPath: "/admin/users",
    children: [
      { key: "/admin/users", label: "Пользователи" },
      { key: "/admin/groups", label: "Группы" },
      { key: "/admin/contracts", label: "Договора" },
      { key: "/admin/integrations", label: "Интеграции" },
      { key: "/admin/mail", label: "Почта" },
      { key: "/admin/templates", label: "Шаблоны" },
    ],
  },
  {
    key: "/settings",
    icon: <SettingOutlined />,
    label: "Настройки",
    defaultPath: "/settings/profile",
    children: [{ key: "/settings/profile", label: "Профиль" }],
  },
  {
    key: "docs",
    icon: <QuestionCircleOutlined />,
    label: "Документация",
    defaultPath: "",
    externalUrl: "https://metrica.pro/ais/docs/getting-started",
  },
];

export function getNavSections(user?: AdminUser): NavSection[] {
  const role = resolveUserRole(user);
  const isAdmin = role === "admin";

  return navSections.filter((section) => isAdmin || section.key !== "/admin");
}

export function findCurrentSection(
  pathname: string,
  sections: NavSection[],
): NavSection | undefined {
  return sections.find(
    (section) => pathname === section.key || pathname.startsWith(`${section.key}/`),
  );
}

export function getSiderItems(sections: NavSection[]): MenuItem[] {
  return sections.map((section) => ({
    key: section.key,
    icon: section.icon,
    label: section.tourId ? (
      <span data-tour-id={section.tourId}>{section.label}</span>
    ) : (
      section.label
    ),
    title: section.label,
  }));
}
